<template>
  <v-navigation-drawer
    app
    :mini-variant="isMinimal && !$vuetify.breakpoint.lgAndUp"
    :width="navbarWidth"
    :mini-variant-width="navbarMinWidth"
    v-model="$store.state.showDrawer"
    enable-resize-watcher
    :right="$store.state.rtl"
    :permanent="$vuetify.breakpoint.lgAndUp"
    :touchless="true"
    class="modern-sidebar"
  >
    <div class="d-flex flex-column fill-height">
      <!-- Brand Logo Header -->
      <div class="sidebar-header d-flex align-center justify-space-between px-5 py-4">
        <img
          :src="
            $vuetify.theme.dark
              ? require('@/app/core-module/assets/images/LOGO_DARK_MODE.png')
              : require('@/app/core-module/assets/images/LOGO.png')
          "
          alt="ZeroSuite Logo"
          class="hand-cursor"
          style="max-width: 145px; height: auto; object-fit: contain;"
          @click="redirect({ routeName: 'Home' })"
        />

        <!-- minimise button on tablet/mobile -->
        <v-btn
          v-if="!$vuetify.breakpoint.lgAndUp"
          icon
          small
          @click="isMinimal = !isMinimal"
        >
          <v-icon color="textLight" small>
            {{
              !$store.state.rtl
                ? isMinimal
                  ? "mdi-chevron-double-right"
                  : "mdi-chevron-double-left"
                : !isMinimal
                ? "mdi-chevron-double-right"
                : "mdi-chevron-double-left"
            }}
          </v-icon>
        </v-btn>
      </div>

      <v-divider class="mx-4 mb-2" style="border-color: rgba(15, 23, 42, 0.06);" />

      <!-- Navigation Items -->
      <div class="sidebar-nav-list flex-grow-1 px-3 py-1">
        <div
          v-for="(item, index) in items"
          :key="`navbar_${item.routeName}_${index}`"
          class="nav-pill d-flex align-center px-3 my-1 hand-cursor"
          :class="
            item.routeName === $route.name ||
            item.relatedRouteNames.includes($route.name)
              ? 'nav-pill--active'
              : 'nav-pill--inactive'
          "
          @click="redirect(item)"
        >
          <v-icon
            size="20"
            class="nav-pill__icon me-3"
            v-text="item.icon ? item.icon.trim() : ''"
          />
          <span
            v-if="$vuetify.breakpoint.lgAndUp || !isMinimal"
            class="nav-pill__label text-truncate"
          >
            {{ item.name }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-5 py-3 mt-auto">
        <v-divider class="mb-3" style="border-color: rgba(15, 23, 42, 0.06);" />
        <div class="d-flex align-center justify-space-between caption sidebar-footer-text">
          <span>{{ $t("poweredBy") }}</span>
          <strong class="primary--text">ZeroSuite</strong>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import {
  hasPermission,
  isAdmin,
  isEmployee,
  authUser,
} from "@/app/user-module/helpers/authHelper";

export default {
  data() {
    return {
      isMinimal: false,
      version: "1.0",
      // selectedTab: 0,
      right: null,
    };
  },
  created() {
    //update languange
    if (this.$route.params.lang === "ar") {
      // i18n.locale = "ar";
      this.$vuetify.rtl = true;
      this.$store.commit("UPDATE_RTL", true);
    } else {
      // i18n.locale = "en";
      this.$vuetify.rtl = false;
      this.$store.commit("UPDATE_RTL", false);
    }
  },
  computed: {
    avatar() {
      return this.$store.state.authenticatedUser.avatar;
    },
    navbarWidth() {
      return !this.$vuetify.breakpoint.lgAndUp ? "230px" : "230px";
    },
    navbarMinWidth() {
      return "72px";
    },
    selectedTabComputed: {
      get() {
        return this.items.findIndex(
          item =>
            item.routeName === this.$route.name ||
            item.relatedRouteNames.includes(this.$route.name)
        );
      },
      // set(value) {
      //   this.selectedTab = value;
      // },
    },
    items() {
      const currentRole = this.$store.state.authenticatedUserRole;
      const currentPermissions = this.$store.state.authenticatedUserPermissions;
      const isOrganizationManager =
        this.$store.state.authenticatedUser?.isOrganizationManager;

      const canShowMenu = item => {
        const restrictOrganizationManager =
          item.restrictOrganizationManager && isOrganizationManager;

        return restrictOrganizationManager || item.requiredPermissions?.length
          ? hasPermission({
              currentRole,
              currentPermissions,
              requiredPermission: item.requiredPermissions,
              requiredAll: item.requiredAll,
              restrictOrganizationManager,
            })
          : true;
      };

      const items = [
        {
          name: this.$t("navBarItems.home"),
          icon: " mdi-home-outline",
          routeName: "Home",
          relatedRouteNames: [],
        },
        {
          name: this.$t("navBarItems.courses"),
          icon: "mdi-book-open-page-variant-outline",
          routeName: "Courses",
          relatedRouteNames: ["CourseDetails"],
          requiredPermissions: ["GET_TRAININGS"],
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("user.registration.registrations"),
          icon: "mdi-file-plus-outline",
          routeName: "Registrations",
          relatedRouteNames: ["Registration"],
          requiredPermissions: ["GET_TRAINING_REGISTRATIONS"],
        },
        {
          name: this.$t("quiz.quizzes"),
          icon: "mdi-comment-question-outline",
          routeName: "Quizzes",
          relatedRouteNames: ["QuizDetails"],
          requiredPermissions: ["GET_QUIZZES"],
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("reports.reports"),
          icon: "mdi-file-chart-outline",
          routeName: "Reports",
          relatedRouteNames: [],
          requiredPermissions: [
            "GET_REGISTRATION_REPORTS",
            "GET_REVENUE_REPORTS",
            "GET_QUIZ_REPORTS",
            "GET_ASSESSMENT_REPORTS",
            "GET_FEEDBACK_REPORTS",
            "GET_TRAINING_MATRIX_REPORTS",
          ],
          requiredAll: false,
        },
        {
          name: this.$t("user.registration.attendance"),
          icon: "mdi-account-check-outline",
          routeName: "Attendance",
          relatedRouteNames: [],
          requiredPermissions: ["GET_TRAINING_REGISTRATION_ATTENDANCE"],
          requiredAll: false,
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("user.certificatesAndProfileCard.certificates"),
          icon: "mdi-certificate-outline",
          routeName: "Certificates",
          relatedRouteNames: [],
          requiredPermissions: [
            "DOWNLOAD_CERTIFICATE",
            "DOWNLOAD_PROFILE_CARD",
          ],
          requiredAll: false,
        },
        {
          name: this.$t("invoice.invoices"),
          icon: "mdi-file-document-outline",
          routeName: "Invoices",
          relatedRouteNames: [],
          requiredPermissions: ["GENERATE_INVOICE", "DOWNLOAD_INVOICE"],
          requiredAll: false,
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("user.subRoles.subRoles"),
          icon: "mdi-account-key-outline",
          routeName: "EmployeesRoles",
          relatedRouteNames: ["EmployeeProfile"],
          requiredPermissions: ["GET_EMPLOYEES"],
        },
        {
          name: this.$t("organization.organizations"),
          icon: "mdi-domain",
          routeName: "Organizations",
          relatedRouteNames: ["OrganizationProfile"],
          requiredPermissions: [
            "CREATE_ORGANIZATION",
            "UPDATE_ORGANIZATION",
            "ENABLE_DISABLE_ORGANIZATION",
            "DELETE_ORGANIZATION",
          ],
          requiredAll: false,
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("trainingCategory.trainingCategories"),
          icon: "mdi-shape-outline",
          routeName: "Categories",
          relatedRouteNames: [],
          requiredPermissions: [
            "CREATE_TRAINING_CATEGORY",
            "UPDATE_TRAINING_CATEGORY",
            "ENABLE_DISABLE_TRAINING_CATEGORY",
            "DELETE_TRAINING_CATEGORY",
          ],
          requiredAll: false,
          restrictOrganizationManager: true,
        },
        {
          name: this.$t("notifications.notifications"),
          icon: "mdi-bell-outline",
          routeName: "Notifications",
          relatedRouteNames: [],
          restrictOrganizationManager: true,
        },
        // {
        //   name: this.$t("navBarItems.profile"),
        //   icon: "mdi-account-circle-outline",
        //   routeName: "Profile",
        //   relatedRouteNames: [],
        // },
        {
          name: this.$t("navBarItems.settings"),
          icon: "mdi-tune-vertical-variant",
          routeName: "Settings",
          relatedRouteNames: ["EmployeeBasicProfile"],
        },
      ];

      return items.reduce((result, item) => {
        if (canShowMenu(item)) result.push(item);
        return result;
      }, []);
    },
  },
  methods: {
    redirect(item) {
      if (item.routeName !== this.$route.name) {
        if (item.routeName === "Settings") {
          if (isAdmin() && this.$route.name !== item.routeName)
            this.$router.push({ name: item.routeName });
          else if (isEmployee() && this.$route.name !== "EmployeeBasicProfile")
            this.$router.push({
              name: "EmployeeBasicProfile",
              params: { userId: authUser()._id },
            });
        } else this.$router.push({ name: item.routeName });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
>>> .v-navigation-drawer__border {
  background-color: rgba(15, 23, 42, 0.06) !important;
}

>>> .v-navigation-drawer__content {
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.sidebar-nav-list {
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-pill {
  height: 40px;
  border-radius: 10px;
  transition: all 0.18s ease;
  position: relative;
  user-select: none;
}

.nav-pill__label {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.nav-pill--inactive {
  color: #475569;
  .nav-pill__icon {
    color: #64748b !important;
  }
  &:hover {
    background-color: #f8fafc;
    color: #0f172a;
    .nav-pill__icon {
      color: #2563eb !important;
    }
  }
}

.nav-pill--active {
  background-color: #eff6ff;
  color: #2563eb;
  font-weight: 600;
  .nav-pill__label {
    font-weight: 600;
  }
  .nav-pill__icon {
    color: #2563eb !important;
  }
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3.5px;
    border-radius: 0 4px 4px 0;
    background-color: #2563eb;
  }
}

.sidebar-footer-text {
  color: #64748b;
}
</style>
